import React from "react";
import { ClockButton } from "./clockButton";
import { getHours, getMinutes } from "date-fns";
import { Button, Input, InputGroup, InputLabel, Modal, Spacer, Text } from "..";
import Box from "react-raster";
import { theme } from "src/config";
import styled from "styled-components";
import { Colors } from "src/types";

interface ITimePickerProps {
  cancelLabel?: string;
  confirmLabel?: string;
  disabled?: boolean;
  label?: string;
  labelProps?: string;
  value: Date;
  hideIcon?: boolean;
  onChange?(time: string): void;
}

const TimeButton = styled(Button)`
  font-weight: ${theme.font.weight.bold};
  font-size: 2.2rem;
`;

export const TimePicker = (props: ITimePickerProps): JSX.Element => {
  const clock_radius = 130;

  const { label, labelProps, disabled, onChange, value, hideIcon } = props;

  const { cancelLabel, confirmLabel } = {
    cancelLabel: props.cancelLabel || "Cancel",
    confirmLabel: props.confirmLabel || "OK"
  };

  const [minute, setMinute] = React.useState<number>(
    (value && (Math.ceil(getMinutes(value) / 5) * 5) % 60) || 0
  );
  const [hour, setHour] = React.useState<number>(
    (value && getHours(value)) || 0
  );
  const [hourMode, setHourMode] = React.useState<boolean>(true);
  const [dlgOpen, setDlgOpen] = React.useState<boolean>(false);

  const confirm = (h: number, m: number): void => {
    const timeString =
      String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
    onChange && onChange(timeString);
    close();
  };

  const close = (): void => {
    setHourMode(true);
    setDlgOpen(false);
  };

  const renderClockButtons = (): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    if (hourMode) {
      for (let i = 1; i <= 12; i++) {
        elements.push(
          <ClockButton
            key={(i % 12) + "am"}
            idx={i % 12}
            radius={clock_radius}
            numItems={12}
            onClick={() => {
              setHour(i);
              setHourMode(false);
            }}
            selected={hour === i}
          >
            {i}
          </ClockButton>
        );
      }
      for (let i = 1; i <= 12; i++) {
        elements.push(
          <ClockButton
            key={String((i + 12) % 24).padStart(2, "0") + "pm"}
            idx={i % 12}
            radius={clock_radius * 0.7}
            numItems={12}
            onClick={() => {
              setHour(i + 12);
              setHourMode(false);
            }}
            selected={hour === i + 12}
          >
            {String((i + 12) % 24).padStart(2, "0")}
          </ClockButton>
        );
      }
    } else {
      for (let i = 0, j = 0; i <= 60; i += 5, j++) {
        elements.push(
          <ClockButton
            key={j + "min"}
            idx={j % 12}
            radius={clock_radius * 0.95}
            numItems={12}
            onClick={async () => {
              setMinute(i % 60);
              confirm(hour, i % 60);
            }}
            selected={minute === i}
          >
            {String(i % 60).padStart(2, "0")}
          </ClockButton>
        );
      }
    }
    return elements;
  };

  const getValue = (hour: number, minute: number): string => {
    if (!value) return "";
    return (
      String(hour).padStart(2, "0") + ":" + String(minute).padStart(2, "0")
    );
  };
  return (
    <>
      <Box>
        <InputGroup>
          <InputLabel focused={dlgOpen || Boolean(value)}>{label}</InputLabel>
          <Input
            disabled={disabled}
            icons={[
              {
                iconId: "watch", action: () => setDlgOpen(true)
              }
            ]}
            value={getValue(hour, minute) + "  "}
            onClick={() => setDlgOpen(true)}
          />
        </InputGroup>
      </Box>
      <Modal show={dlgOpen} noBorder>
        <Box width={"30rem"} height={"38rem"}>
          <Box
            background={theme.color.primary8}
            color="white"
            alignItems="center"
            justifyContent="center"
            display="flex"
            flexDirection="row"
            height="100%"
            fontWeight="bold"
          >
            <TimeButton variant="text" color={"white"} onClick={() => setHourMode(true)}>
              {`${String(hour % 24).padStart(2, "0")}`}
            </TimeButton>
            <Text size="large" color="white">:</Text>
            <TimeButton variant="text" color={"white"} onClick={() => setHourMode(false)}>
              {`${String(minute).padStart(2, "0")}`}
            </TimeButton>
          </Box>
          <Box className="tp-body" background="white">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop="0.5rem"
            >
              <Box
                width={"20rem"}
                height={"20rem"}
                position="relative"
                marginTop="1rem"
              >
                {renderClockButtons()}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gridRowGap="1rem"
          marginTop="1.5rem"
          padding="1rem"
          paddingLeft="4rem"
          paddingRight="4rem"
          borderTop="1px solid #DDDDDD"
        >
          <Button
            variant="outlined"
            size="default"
            color={"black"}
            onClick={close}
          >
            {cancelLabel}
          </Button>
          <Spacer size="large" />
          <Button
            variant="primary"
            size="default"
            color={"black"}
            onClick={() => confirm(hour, minute)}
          >
            {confirmLabel}
          </Button>
        </Box>
      </Modal>
    </>
  );
};
